/**
 * usePhantomFeed.ts · WebSocket-first Phantom Bus feed with polling fallback
 *
 * Contract:
 *   1. Open ws://<base>/ws/phantom on mount.
 *   2. Every WS message is a `PhantomMatch` (or `{type:"pong"}` control).
 *   3. On close/error, degrade to polling GET /api/phantom/history?limit=20
 *      every 15s.
 *   4. Auto-reconnect the WebSocket with exponential backoff (1s → 8s cap).
 *   5. `triggerManual(stopId, pax)` POSTs to /api/phantom/request and
 *      returns the response envelope (the WS should ALSO push the same
 *      event, but callers get an immediate handle for optimistic UI).
 *   6. Track `activeMatches` = last event per (matched_driver_id | virtual_bus_id)
 *      that hasn't `failed` — used to drive PhantomLayer's TripsLayer.
 *   7. Track `recentMatches` = last 20 events regardless of status — used
 *      by PhantomEventStrip + PhantomHistoryList.
 *
 * `connectionState` mirrors `useLiveFeed`'s state machine so the
 * bottom-strip can render an unobtrusive dot in the same visual language.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getBaseUrl, getWebSocketUrl, isAbortError } from "../lib/api";
// ---- helpers ------------------------------------------------------
const RING_DEFAULT = 20;
/** Best-effort coerce of an unknown payload into a `PhantomMatch`. */
function coerceMatch(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const o = raw;
    if (typeof o.id !== "number")
        return null;
    if (typeof o.status !== "string")
        return null;
    const status = o.status;
    if (status !== "matched" && status !== "spawned" && status !== "failed") {
        return null;
    }
    const polyline = Array.isArray(o.driver_polyline)
        ? o.driver_polyline.filter((p) => Array.isArray(p) &&
            p.length === 2 &&
            typeof p[0] === "number" &&
            typeof p[1] === "number")
        : null;
    return {
        id: o.id,
        ts: typeof o.ts === "string"
            ? o.ts
            : new Date().toISOString(),
        corridor: typeof o.corridor === "string" ? o.corridor : "AS-4",
        trigger_stop_id: typeof o.trigger_stop_id === "string" ? o.trigger_stop_id : "unknown",
        waiting_pax: typeof o.waiting_pax === "number" ? o.waiting_pax : 0,
        matched_driver_id: typeof o.matched_driver_id === "string" ? o.matched_driver_id : null,
        virtual_bus_id: typeof o.virtual_bus_id === "string" ? o.virtual_bus_id : null,
        spawn_latency_s: typeof o.spawn_latency_s === "number" ? o.spawn_latency_s : 0,
        status,
        driver_polyline: polyline && polyline.length > 0 ? polyline : null,
        eta_s: typeof o.eta_s === "number" ? o.eta_s : null,
        simulated: typeof o.simulated === "boolean" ? o.simulated : true,
    };
}
/** Stable key per phantom "actor" — used to dedupe activeMatches. */
function actorKey(m) {
    return m.virtual_bus_id ?? m.matched_driver_id ?? `phantom-${m.id}`;
}
/** Merge a fresh event into the ring buffer (most-recent-first). */
function mergeRing(prev, next, cap) {
    const existing = prev.findIndex((p) => p.id === next.id);
    if (existing >= 0) {
        const copy = prev.slice();
        copy[existing] = next;
        return copy;
    }
    const merged = [next, ...prev];
    return merged.length > cap ? merged.slice(0, cap) : merged;
}
/** Compute active phantoms: most recent non-`failed` per actor, still animating. */
function computeActive(all) {
    const byActor = new Map();
    // Iterate oldest → newest so the newest overwrites; but keep at most one per actor.
    for (const m of [...all].reverse()) {
        if (m.status === "failed") {
            byActor.delete(actorKey(m));
            continue;
        }
        if (!m.driver_polyline || m.driver_polyline.length < 2)
            continue;
        byActor.set(actorKey(m), m);
    }
    return Array.from(byActor.values());
}
// ---- hook ---------------------------------------------------------
export function usePhantomFeed(options = {}) {
    const { wsUrl: providedUrl, 
    // Q6: match the live-feed hook's 3s cadence so phantom events don't
    // lag 12s behind vehicle positions during WS reconnect.
    pollingIntervalMs = 3_000, backoffBaseMs = 1_000, backoffMaxMs = 8_000, pingIntervalMs = 30_000, enabled = true, ringSize = RING_DEFAULT, corridor = "AS-4", } = options;
    const [recentMatches, setRecentMatches] = useState([]);
    const [connectionState, setConnectionState] = useState("connecting");
    const [lastError, setLastError] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const pingTimerRef = useRef(null);
    const pollTimerRef = useRef(null);
    const abortRef = useRef(null);
    const attemptsRef = useRef(0);
    const mountedRef = useRef(true);
    const wsSupportedRef = useRef(typeof WebSocket !== "undefined");
    const wsUrl = useMemo(() => providedUrl ?? getWebSocketUrl("/ws/phantom"), [providedUrl]);
    const ingest = useCallback((raw) => {
        const m = coerceMatch(raw);
        if (!m)
            return;
        setRecentMatches((prev) => mergeRing(prev, m, ringSize));
    }, [ringSize]);
    const clearTimers = useCallback(() => {
        if (reconnectTimerRef.current !== null) {
            window.clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        if (pingTimerRef.current !== null) {
            window.clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
        }
        if (pollTimerRef.current !== null) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
        }
    }, []);
    // Poll fallback — REST /api/phantom/history every N ms while WS is down.
    const startPolling = useCallback(() => {
        if (pollTimerRef.current !== null)
            return;
        const tick = async () => {
            if (!mountedRef.current)
                return;
            abortRef.current?.abort();
            const ac = new AbortController();
            abortRef.current = ac;
            try {
                const res = await fetch(`${getBaseUrl()}/api/phantom/history?limit=${ringSize}`, { signal: ac.signal, headers: { Accept: "application/json" } });
                if (!res.ok)
                    throw new Error(`HTTP ${res.status}`);
                const data = (await res.json());
                if (!mountedRef.current)
                    return;
                if (Array.isArray(data)) {
                    // Newest-first per contract; ingest oldest → newest so the ring
                    // buffer preserves recency correctly.
                    const coerced = data
                        .map((d) => coerceMatch(d))
                        .filter((m) => m !== null)
                        .reverse();
                    if (coerced.length > 0) {
                        setRecentMatches((prev) => {
                            let out = prev;
                            for (const m of coerced)
                                out = mergeRing(out, m, ringSize);
                            return out;
                        });
                    }
                }
                setLastError(null);
                setConnectionState((prev) => (prev === "connected" ? prev : "polling"));
            }
            catch (err) {
                if (isAbortError(err) || !mountedRef.current)
                    return;
                setConnectionState("disconnected");
                setLastError(err instanceof Error ? err.message : String(err));
            }
        };
        void tick();
        pollTimerRef.current = window.setInterval(tick, pollingIntervalMs);
    }, [pollingIntervalMs, ringSize]);
    // Forward declare so `connect` can reference it.
    const scheduleReconnectRef = useRef(() => undefined);
    const connect = useCallback(() => {
        if (!enabled)
            return;
        if (!wsSupportedRef.current) {
            startPolling();
            return;
        }
        if (wsRef.current) {
            try {
                wsRef.current.close();
            }
            catch {
                /* noop */
            }
            wsRef.current = null;
        }
        setConnectionState("connecting");
        let ws;
        try {
            ws = new WebSocket(wsUrl);
        }
        catch (err) {
            setConnectionState("disconnected");
            setLastError(err instanceof Error ? err.message : String(err));
            startPolling();
            scheduleReconnectRef.current();
            return;
        }
        wsRef.current = ws;
        ws.onopen = () => {
            if (!mountedRef.current)
                return;
            setConnectionState("connected");
            setLastError(null);
            attemptsRef.current = 0;
            // Stop polling — WS wins.
            if (pollTimerRef.current !== null) {
                window.clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
            if (pingTimerRef.current !== null) {
                window.clearInterval(pingTimerRef.current);
            }
            pingTimerRef.current = window.setInterval(() => {
                try {
                    ws.send(JSON.stringify({ type: "ping" }));
                }
                catch {
                    /* socket may already be closed */
                }
            }, pingIntervalMs);
        };
        ws.onmessage = (evt) => {
            if (!mountedRef.current)
                return;
            try {
                const parsed = JSON.parse(typeof evt.data === "string" ? evt.data : String(evt.data));
                // Ignore pong control frames.
                if (parsed &&
                    typeof parsed === "object" &&
                    parsed.type === "pong") {
                    return;
                }
                // Backend may send either a raw PhantomMatch or an envelope
                // `{event: PhantomMatch}` — handle both.
                if (parsed &&
                    typeof parsed === "object" &&
                    "event" in parsed) {
                    ingest(parsed.event);
                }
                else {
                    ingest(parsed);
                }
            }
            catch (err) {
                setLastError(err instanceof Error ? err.message : String(err));
            }
        };
        ws.onerror = () => {
            if (!mountedRef.current)
                return;
            setLastError("WebSocket error");
        };
        ws.onclose = () => {
            if (!mountedRef.current)
                return;
            wsRef.current = null;
            if (pingTimerRef.current !== null) {
                window.clearInterval(pingTimerRef.current);
                pingTimerRef.current = null;
            }
            setConnectionState((prev) => prev === "connected" ? "polling" : "disconnected");
            startPolling();
            scheduleReconnectRef.current();
        };
    }, [enabled, wsUrl, pingIntervalMs, startPolling, ingest]);
    const scheduleReconnect = useCallback(() => {
        if (!enabled)
            return;
        if (reconnectTimerRef.current !== null)
            return;
        const attempt = attemptsRef.current + 1;
        attemptsRef.current = attempt;
        const delay = Math.min(backoffMaxMs, backoffBaseMs * 2 ** (attempt - 1));
        reconnectTimerRef.current = window.setTimeout(() => {
            reconnectTimerRef.current = null;
            connect();
        }, delay);
    }, [backoffBaseMs, backoffMaxMs, enabled, connect]);
    // Keep the ref in sync so ws.onclose can call it.
    useEffect(() => {
        scheduleReconnectRef.current = scheduleReconnect;
    }, [scheduleReconnect]);
    // Manual trigger — POSTs and ingests the response into the ring buffer.
    const triggerManual = useCallback(async (stopId, pax) => {
        const url = `${getBaseUrl()}/api/phantom/request`;
        const body = {
            corridor,
            stop_id: stopId,
            waiting_pax: pax,
        };
        const res = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const txt = await res.text().catch(() => "");
            throw new Error(`POST /api/phantom/request failed HTTP ${res.status} ${txt}`);
        }
        const raw = (await res.json());
        const coerced = coerceMatch(raw);
        if (!coerced) {
            throw new Error("Malformed /api/phantom/request response");
        }
        if (mountedRef.current) {
            setRecentMatches((prev) => mergeRing(prev, coerced, ringSize));
            // Manual trigger implies we HAVE a live REST channel — if the WS
            // is down we can at least reflect polling state.
            setConnectionState((prev) => prev === "connected" ? prev : "polling");
            setLastError(null);
        }
        return coerced;
    }, [corridor, ringSize]);
    // Boot / teardown.
    useEffect(() => {
        if (!enabled) {
            setConnectionState("disconnected");
            return;
        }
        mountedRef.current = true;
        // Kick off an immediate REST hydration so the strip has something
        // to show before the WS opens.
        (async () => {
            try {
                const ac = new AbortController();
                abortRef.current = ac;
                const res = await fetch(`${getBaseUrl()}/api/phantom/history?limit=${ringSize}`, { signal: ac.signal, headers: { Accept: "application/json" } });
                if (!res.ok)
                    return;
                const data = (await res.json());
                if (!mountedRef.current)
                    return;
                if (Array.isArray(data)) {
                    const coerced = data
                        .map((d) => coerceMatch(d))
                        .filter((m) => m !== null)
                        .reverse();
                    if (coerced.length > 0) {
                        setRecentMatches((prev) => {
                            let out = prev;
                            for (const m of coerced)
                                out = mergeRing(out, m, ringSize);
                            return out;
                        });
                    }
                }
            }
            catch (err) {
                if (isAbortError(err) || !mountedRef.current)
                    return;
                // Silent — the WS + polling loop will publish an error next.
            }
        })();
        connect();
        return () => {
            mountedRef.current = false;
            clearTimers();
            if (wsRef.current) {
                try {
                    wsRef.current.close();
                }
                catch {
                    /* noop */
                }
                wsRef.current = null;
            }
        };
    }, [enabled, connect, clearTimers, ringSize]);
    const activeMatches = useMemo(() => computeActive(recentMatches), [recentMatches]);
    return useMemo(() => ({
        connectionState,
        recentMatches,
        activeMatches,
        lastError,
        triggerManual,
    }), [connectionState, recentMatches, activeMatches, lastError, triggerManual]);
}
